import { raceForKey, type MatchPlayer } from '@cratered/shared';
import { boatContains, deckY } from './boats';
import {
  INK,
  drawAirBomb,
  drawBigGrenade,
  drawBolt,
  drawBoulder,
  drawBurst,
  drawCatapult,
  drawCloud,
  drawCluster,
  drawGrave,
  drawGrenade,
  drawHook,
  drawJetski,
  drawMassive,
  drawMine,
  drawPalm,
  drawPlane,
  drawRocket,
  drawRubberBall,
  drawShell,
  drawSplash,
  drawSticky,
  drawSun,
  drawTennisBall,
  drawTnt,
  drawTunneler,
  ink,
  inkLine,
  luminance,
  mix,
  rr,
  shade,
} from './cartoon';
import { Backdrop } from './backdrop';
import { LOG_BOTTOM, drawBoats } from './drawBoats';
import { GroundDecor, type GroundArt } from './ground';
import { paintLevel, type LevelDef } from './levels';
import { lookFor } from './look';
import { drawMole, type HeldItem, type MoleLook, type MolePose } from './mole';
import { clamp, mulberry32 } from './rng';
import { Scenery, type SceneryView } from './scenery';
import { UNIT_H, UNIT_HALF_W, catapultNear, platformSpot, strikeSpot, teleportSpot, type Unit, type ViewState, type VisualEvent } from './sim';
import { ParticleSystem } from './trails';
import { ACTIONS, CATAPULT_CUP_H, DIG_BITES, DIG_RADIUS, DIG_STEP, PLATFORM_H, PLATFORM_W, STRIKE_GAP, launchSpeed } from './weapons';

export const TEAM_COLORS = ['#f5a524', '#3b8bff', '#2fbf71', '#ff6fae', '#b388ff', '#ff5d5d', '#2ad4d4', '#f4f4f4'];
export const TEAM_GLYPHS = ['■', '●', '▲', '◆', '★', '⬟', '⬢', '✚'];
const ENEMY_COLOR = '#c0392b';

/**
 * The water is painted at the level's waterline itself: raft decks sit 40 px above it, so the
 * lower log is in the water, and moles are only out once they are DROWN_DEPTH under it.
 */
const WATER_DROP = 0;
/** Spatial frequency of the backdrop islands: humps about 900 world px wide with sea between. */
const ISLAND_K = 0.0035;

const ROPE = '#ecd79c';

interface Effect {
  kind:
    | 'explosion' | 'damage' | 'shot' | 'splash' | 'kick' | 'grave' | 'bubble' | 'hook'
    | 'strike' | 'teleport' | 'bolt' | 'splat' | 'boom' | 'catapult';
  x: number;
  y: number;
  x1?: number;
  y1?: number;
  r?: number;
  text?: string;
  color?: string;
  unitId?: string;
  dir?: number;
  /** Which gun fired a 'shot', for its look. */
  weapon?: string;
  /** Which catapult a 'catapult' swing belongs to. */
  id?: number;
  /** Which way a 'bolt' points. */
  angle?: number;
  life: number;
  maxLife: number;
}

interface Cloud {
  x: number;
  y: number;
  s: number;
  depth: number;
}

export interface AimPreview {
  unitId: string;
  angle: number;
  power: number;
  kind: 'throw' | 'gun';
}

export interface RenderInput {
  view: ViewState;
  players: MatchPlayer[];
  localPlayerId: string;
  /** Brawl: the local mole. Stands in for the active mole (camera, aim preview, markers) while no turn names one. */
  focusUnitId: string | null;
  aim: AimPreview | null;
  /** World coords of the pointer (crosshair for the gun, hover for selection). */
  pointer: { x: number; y: number } | null;
  /** Highlight the local player's selectable moles. */
  selecting: boolean;
  pip: boolean;
}

/**
 * The catapult arm's travel through a shot, 0 cocked back and 1 against the stop: it snaps
 * forward, shudders on the stop, then is wound back to `rest` (cocked again, or left forward
 * when the pile is empty and there is nothing to wind for).
 */
function catapultSwing(t: number, rest: number): number {
  if (t < 0.3) {
    const k = t / 0.3;
    return 1 - (1 - k) * (1 - k);
  }
  if (t < 0.55) {
    const k = (t - 0.3) / 0.25;
    return 1 - 0.05 * Math.sin(k * Math.PI * 3) * (1 - k);
  }
  const k = (t - 0.55) / 0.45;
  const ease = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
  return 1 + (rest - 1) * ease;
}

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private particles = new ParticleSystem();
  private effects: Effect[] = [];
  private rng = mulberry32(42);
  private lastFrame = performance.now();
  private looks = new Map<string, MoleLook>();
  private ground: GroundArt | null = null;
  private decor: GroundDecor | null = null;
  private clouds: Cloud[] = [];
  private readonly scenery: Scenery;
  private readonly backdrop: Backdrop;
  private cam = { x: 0, y: 0, zoom: 1 };
  private shake = 0;
  /** Brawl: the local mole's current cooldown at its longest, for the ring over its head. */
  private coolPeak = 0;
  private dpr = 1;
  private cw = 1;
  private ch = 1;
  private readonly bright: boolean;

  constructor(private readonly canvas: HTMLCanvasElement, private readonly level: LevelDef) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('no 2d context');
    this.ctx = ctx;
    this.cam.x = level.width / 2;
    this.cam.y = level.height * 0.6;
    this.bright = luminance(level.style.sky[1]) > 0.62;
    this.scenery = new Scenery(level);
    this.backdrop = new Backdrop(level.style.art);
    const r = mulberry32(level.width + level.height);
    const n = 6 + Math.round(level.width / 450);
    for (let i = 0; i < n; i++) {
      this.clouds.push({ x: r() * (level.width + 1200) - 600, y: 40 + r() * 300, s: 26 + r() * 34, depth: 0.25 + r() * 0.25 });
    }
  }

  setPlayers(players: MatchPlayer[]): void {
    for (const p of players) {
      this.looks.set(p.id, lookFor(p.equipped, TEAM_COLORS[p.team % TEAM_COLORS.length]));
    }
  }

  screenToWorld(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const px = (clientX - rect.left) * this.dpr;
    const py = (clientY - rect.top) * this.dpr;
    return { x: (px - this.cw / 2) / this.cam.zoom + this.cam.x, y: (py - this.ch / 2) / this.cam.zoom + this.cam.y };
  }

  /** World → CSS px relative to the canvas element (for DOM overlays). */
  worldToScreen(x: number, y: number): { x: number; y: number } {
    return {
      x: ((x - this.cam.x) * this.cam.zoom + this.cw / 2) / this.dpr,
      y: ((y - this.cam.y) * this.cam.zoom + this.ch / 2) / this.dpr,
    };
  }

  private fit(): void {
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width * this.dpr));
    const h = Math.max(1, Math.round(rect.height * this.dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.cw = w;
    this.ch = h;
  }

  private updateCamera(view: ViewState, dt: number, input: RenderInput): void {
    const focusUnitId = input.focusUnitId;
    // About 1100 world px across the screen, so a mole is ~60px tall on a laptop. Between turns
    // the camera pulls out to the whole map, however small that makes it, so the next move is
    // picked knowing where everyone stands; the next turn eases back in to its mole.
    const between = view.turn.phase === 'between';
    const fitZoom = Math.min(this.cw / this.level.width, this.ch / this.level.height) * 0.96;
    const targetZoom = between ? fitZoom : clamp(this.cw / 1100, 0.45, 1.9);
    this.cam.zoom += (targetZoom - this.cam.zoom) * (1 - Math.pow(0.9, dt));

    let fx = this.cam.x;
    let fy = this.cam.y;
    let ease = 0.08;
    const proj = view.projectiles.find((p) => p.alive);
    const flying = view.units.find((u) => u.alive && u.ragdoll && !u.onGround);
    const activeId = view.turn.unitId ?? focusUnitId;
    const active = view.units.find((u) => u.id === activeId && u.alive);
    // Aiming an airstrike: the camera goes where the bombs will, not where the mole stands, so the
    // mark can be walked onto a target across the map.
    const aimed = active ? this.aimedStrike(view, input, active) : null;
    if (between) {
      fx = this.level.width / 2;
      fy = this.level.height / 2;
      ease = 0.1;
    } else if (proj) {
      fx = proj.x + proj.vx * 6;
      fy = proj.y + proj.vy * 4;
      ease = 0.16;
    } else if (aimed) {
      fx = aimed.x;
      fy = aimed.y - 120;
      ease = 0.14;
    } else if (flying) {
      fx = flying.x;
      fy = flying.y;
      ease = 0.14;
    } else if (active) {
      fx = active.x;
      fy = active.y - 80;
      ease = 0.12;
    }
    const halfW = this.cw / this.cam.zoom / 2;
    const halfH = this.ch / this.cam.zoom / 2;
    fx = this.level.width <= halfW * 2 ? this.level.width / 2 : clamp(fx, halfW, this.level.width - halfW);
    fy = this.level.height <= halfH * 2 ? this.level.height / 2 : clamp(fy, halfH, this.level.height - halfH);
    const k = 1 - Math.pow(1 - ease, dt);
    this.cam.x += (fx - this.cam.x) * k;
    this.cam.y += (fy - this.cam.y) * k;
    this.shake *= Math.pow(0.85, dt);
  }

  /** The spot an airstrike is being marked on right now (the local preview, else the turn's aim), or null. */
  private aimedStrike(view: ViewState, input: RenderInput, u: Unit): { x: number; y: number } | null {
    const live = view.turn.phase === 'live';
    const action = live ? u.action : view.turn.phase === 'aim' ? view.turn.action : null;
    if (!action || !ACTIONS[action].strike) return null;
    const local = input.aim && input.aim.unitId === u.id ? input.aim : null;
    if (live && !local) return null;
    const angle = local ? local.angle : view.turn.aimAngle;
    const power = local ? local.power : view.turn.aimPower;
    const x = strikeSpot(view.level, u, angle, power);
    return { x, y: this.groundUnder(view, x) };
  }

  /** Fresh ground art and decor; the view's terrain gets the fill picture to punch craters out of. */
  private rebuildGround(view: ViewState): void {
    this.ground = paintLevel(this.level);
    this.decor = new GroundDecor(this.ground);
    this.attachGround(view);
  }

  private attachGround(view: ViewState): void {
    if (!this.ground || !this.decor) return;
    view.terrain.canvas = this.ground.fill;
    view.terrain.capCanvas = this.ground.cap;
    // Craters (and planks) that happened before we had a picture (a late join, a fight already underway).
    view.terrain.applyMaskToCanvas();
    view.terrain.dirty.length = 0;
    this.decor.resync();
  }

  frame(input: RenderInput): void {
    const { view } = input;
    if (!this.ground || !this.decor) this.rebuildGround(view);
    else if (view.terrain.canvas !== this.ground.fill) this.attachGround(view);
    const decor = this.decor!;
    for (const c of view.terrain.dirty.splice(0)) {
      if (c.r < 0) decor.resync();
      else if (c.fill) decor.build(c);
      else decor.carve(c);
    }
    const now = performance.now();
    const dt = Math.min(50, now - this.lastFrame) / (1000 / 60);
    this.lastFrame = now;

    for (const ev of view.drainVisual()) this.ingest(ev, view);
    for (let i = 0; i < dt; i++) this.particles.update();
    for (const e of this.effects) {
      e.life -= dt;
      // A blown vessel keeps pouring a column of smoke for as long as its fireball lasts.
      if (e.kind === 'boom' && e.life > 0) {
        const r = e.r ?? 60;
        for (let k = 0; k < 2; k++) {
          this.particles.puff(
            e.x + (this.rng() - 0.5) * r, e.y - 10 - this.rng() * r * 0.5, (this.rng() - 0.5) * 0.4, -1.4 - this.rng() * 0.8,
            9 + r / 14, this.rng, this.rng() < 0.5 ? '#4a4d52' : '#6b6f75', 90,
          );
        }
      }
    }
    this.effects = this.effects.filter((e) => e.life > 0);

    for (const p of view.projectiles) {
      if (!p.alive) continue;
      this.particles.spawnTrail(p.trail, p.x, p.y, p.vx, p.vy, this.rng);
      if (p.weapon === 'tunneler') {
        // The machine chuffs out of its stack every few frames and throws dirt off the bit as it bores.
        const len = Math.hypot(p.vx, p.vy) || 1;
        const dx = p.vx / len;
        const dy = p.vy / len;
        // The sprite is mirrored when it heads left so the stack stays on top: the stack's side flips with it.
        const up = dx < 0 ? -1 : 1;
        if (p.age % 9 === 0) this.particles.puff(p.x - dx * 14 + up * dy * 20, p.y - dy * 14 - up * dx * 20, -dx * 0.3, -0.6, 3.5, this.rng, '#6b7079', 26);
        for (let k = 0; k < 2; k++) {
          this.particles.burst(p.x + dx * 34, p.y + dy * 34, 1, this.rng() < 0.5 ? '#7a4a22' : '#a0744a', 2.5, this.rng, 22, 2.5);
        }
      }
      if ((p.weapon === 'rpg' || p.weapon === 'mortar') && this.rng() < 0.9) {
        const len = Math.hypot(p.vx, p.vy) || 1;
        this.particles.puff(p.x - (p.vx / len) * 14, p.y - (p.vy / len) * 14, -p.vx * 0.05, -p.vy * 0.05, 5, this.rng, p.homing ? '#d9a4c2' : '#9aa0a8', 34);
      }
    }
    for (const u of view.units) {
      if (u.alive && u.ragdoll && !u.onGround) this.particles.spawnTrail(view.trailFor(u.playerId), u.x, u.y - UNIT_H / 2, u.vx, u.vy, this.rng);
    }

    this.fit();
    this.updateCamera(view, dt, input);
    const ctx = this.ctx;
    const w = this.cw;
    const h = this.ch;

    const painted = this.drawSky(ctx, now);
    const sceneView = this.sceneryView(now);
    // A painted backdrop brings its own far hills, skyline and props, so the drawn far and mid layers would float
    // over it; only the near props go on top. Caves keep their mid layer: it hangs from the ceiling.
    if (!painted) this.scenery.drawFar(ctx, sceneView);
    if (!painted || this.level.style.horizon === 'cave') this.scenery.drawMid(ctx, sceneView);

    ctx.save();
    const sx = (this.rng() - 0.5) * this.shake * this.cam.zoom;
    const sy = (this.rng() - 0.5) * this.shake * this.cam.zoom;
    ctx.translate(w / 2 + sx, h / 2 + sy);
    ctx.scale(this.cam.zoom, this.cam.zoom);
    ctx.translate(-this.cam.x, -this.cam.y);

    this.drawDeep(ctx);
    ctx.drawImage(this.ground!.fill, 0, 0);
    ctx.drawImage(decor.canvas, 0, 0);
    drawBoats(ctx, view.boats, { now, centerX: this.level.width / 2, particles: this.particles, rng: this.rng });
    this.drawCatapults(ctx, view);
    this.drawGraves(ctx);
    this.drawMines(ctx, view, now);
    this.drawShots(ctx);
    this.drawRopes(ctx, view);
    this.drawUnits(ctx, input);
    this.drawProjectiles(ctx, view, now);
    this.particles.draw(ctx);
    this.drawExplosions(ctx);
    if (this.level.waterY !== null) this.drawWater(ctx, this.level.waterY + WATER_DROP, now);
    // The near scenery sits over the field in screen space, under the aim preview and the labels.
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.scenery.drawFore(ctx, sceneView);
    ctx.restore();
    this.drawGhost(ctx, input);
    this.drawAim(ctx, input, now);
    this.drawBubbles(ctx, view);
    this.drawDamageNumbers(ctx);
    ctx.restore();

    this.drawPlanes(ctx, now);

    // A whisper of vignette so the frame reads as a stage.
    const vg = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.5, w / 2, h / 2, Math.max(w, h) * 0.8);
    vg.addColorStop(0, 'rgba(0,0,0,0)');
    vg.addColorStop(1, 'rgba(0,0,0,0.16)');
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, w, h);
  }

  private ingest(ev: VisualEvent, view: ViewState): void {
    switch (ev.kind) {
      case 'explosion':
        this.effects.push({ kind: 'explosion', x: ev.x, y: ev.y, r: ev.r, life: 26, maxLife: 26 });
        if (ev.r > 12) {
          this.particles.burst(ev.x, ev.y, Math.round(ev.r / 2.5), '#7a4a22', ev.r / 9, this.rng, 36, 3.5);
          this.particles.burst(ev.x, ev.y, Math.round(ev.r / 4), '#ffb347', ev.r / 12, this.rng, 18, 2.5);
          const puffs = 4 + Math.round(ev.r / 18);
          for (let i = 0; i < puffs; i++) {
            const a = this.rng() * Math.PI * 2;
            const d = this.rng() * ev.r * 0.5;
            this.particles.puff(ev.x + Math.cos(a) * d, ev.y + Math.sin(a) * d, Math.cos(a) * 0.5, -0.4, 7 + ev.r / 10, this.rng, '#8d939b', 52);
          }
        }
        break;
      case 'shake':
        this.shake = Math.max(this.shake, ev.power * 3);
        break;
      case 'damage':
        this.effects.push({
          kind: 'damage', x: ev.x + (this.rng() - 0.5) * 16, y: ev.y - 10, text: `-${ev.amount}`,
          color: ev.headshot ? '#ffcc4d' : '#ffffff', life: 70, maxLife: 70,
        });
        break;
      case 'death':
        if (ev.cause === 'damage') {
          this.effects.push({ kind: 'grave', x: ev.x, y: ev.y, color: this.colorForTeam(ev.team, view), life: 1e9, maxLife: 1e9 });
          this.particles.burst(ev.x, ev.y - UNIT_H / 2, 20, this.colorForTeam(ev.team, view), 4, this.rng, 40, 3);
        }
        break;
      case 'splash':
        this.effects.push({ kind: 'splash', x: ev.x, y: ev.y + WATER_DROP, life: 44, maxLife: 44 });
        this.particles.burst(ev.x, ev.y + WATER_DROP, 26, 'rgba(225,245,255,0.95)', 6.5, this.rng, 40, 3);
        break;
      case 'shot': {
        // A harpoon line hangs around; a railgun slug leaves a glow; SMG tracers are gone in a blink.
        const life = ev.weapon === 'harpoon' ? 26 : ev.weapon === 'railgun' ? 30 : ev.weapon === 'smg' ? 7 : 10;
        this.effects.push({ kind: 'shot', x: ev.x0, y: ev.y0, x1: ev.x1, y1: ev.y1, weapon: ev.weapon, life, maxLife: life });
        if (ev.weapon === 'shotgun') this.particles.burst(ev.x0, ev.y0, 10, '#ffe6a0', 5, this.rng, 14, 2.5);
        if (ev.weapon === 'smg') this.particles.burst(ev.x0, ev.y0, 2, '#ffe6a0', 3, this.rng, 8, 1.5);
        if (ev.weapon === 'railgun') {
          // Sparks the whole length of the line: the slug went through everything on it.
          for (let i = 0; i <= 10; i++) {
            const f = i / 10;
            this.particles.burst(ev.x0 + (ev.x1 - ev.x0) * f, ev.y0 + (ev.y1 - ev.y0) * f, 2, i % 2 ? '#9ff3ff' : '#ffffff', 2.5, this.rng, 26, 2);
          }
        }
        break;
      }
      case 'hook':
        this.effects.push({ kind: 'hook', x: ev.x, y: ev.y, life: 40, maxLife: 40 });
        this.particles.burst(ev.x, ev.y, 8, '#7a4a22', 3, this.rng, 24, 2.5);
        break;
      case 'build':
        for (let i = 0; i < 5; i++) this.particles.puff(ev.x + (this.rng() - 0.5) * 40, ev.y, (this.rng() - 0.5) * 0.8, -0.4, 7, this.rng, '#c9bca4', 36);
        break;
      case 'heal':
        this.effects.push({ kind: 'damage', x: ev.x, y: ev.y - 10, text: '+' + ev.amount, color: '#5ef08a', life: 70, maxLife: 70 });
        break;
      case 'dig':
        this.particles.burst(ev.x, ev.y, 14, '#7a4a22', ev.r / 8, this.rng, 32, 3.5);
        for (let i = 0; i < 3; i++) this.particles.puff(ev.x, ev.y, (this.rng() - 0.5) * 0.8, -0.4, 7, this.rng, '#b89a7a', 36);
        break;
      case 'kick':
        this.effects.push({ kind: 'kick', x: ev.x, y: ev.y, dir: ev.dir, weapon: ev.weapon, life: 18, maxLife: 18 });
        break;
      case 'strike':
        this.effects.push({ kind: 'strike', x: ev.x, y: 0, dir: ev.dir, life: 70, maxLife: 70 });
        break;
      case 'teleport':
        this.effects.push({ kind: 'teleport', x: ev.x0, y: ev.y0, x1: ev.x1, y1: ev.y1, life: 40, maxLife: 40 });
        for (const [x, y] of [[ev.x0, ev.y0], [ev.x1, ev.y1]] as [number, number][]) {
          this.particles.burst(x, y - UNIT_H / 2, 18, '#9ff3ff', 4, this.rng, 30, 2.5);
          this.particles.burst(x, y - UNIT_H / 2, 10, '#ffffff', 2.5, this.rng, 24, 2);
        }
        break;
      case 'bolt':
        this.effects.push({ kind: 'bolt', x: ev.x, y: ev.y, angle: ev.angle, life: 180, maxLife: 180 });
        this.particles.burst(ev.x, ev.y, 6, '#7a4a22', 2.5, this.rng, 20, 2);
        break;
      case 'stick':
        this.effects.push({ kind: 'splat', x: ev.x, y: ev.y, life: 24, maxLife: 24 });
        this.particles.burst(ev.x, ev.y, 12, '#7fd93a', 3.5, this.rng, 26, 2.5);
        break;
      case 'boom': {
        // A vessel going up: debris, a sheet of flame and a smoke column that keeps rising while the effect lasts.
        const r = ev.w / 2;
        this.effects.push({ kind: 'boom', x: ev.x, y: ev.y, r, life: 60, maxLife: 60 });
        this.particles.burst(ev.x, ev.y, 40, '#6b4423', r / 9, this.rng, 60, 4);
        this.particles.burst(ev.x, ev.y, 26, '#ffb347', r / 11, this.rng, 30, 3);
        this.particles.burst(ev.x, ev.y, 14, '#4b525b', r / 10, this.rng, 70, 5);
        for (let i = 0; i < 12; i++) {
          this.particles.puff(ev.x + (this.rng() - 0.5) * r * 1.4, ev.y - this.rng() * 30, (this.rng() - 0.5) * 0.8, -0.8, 14 + r / 10, this.rng, '#4a4d52', 80);
        }
        this.shake = Math.max(this.shake, 10);
        break;
      }
      case 'catapult':
        this.effects = this.effects.filter((e) => !(e.kind === 'catapult' && e.id === ev.id));
        this.effects.push({ kind: 'catapult', x: 0, y: 0, id: ev.id, life: 25, maxLife: 25 });
        break;
      case 'say':
        this.effects = this.effects.filter((e) => !(e.kind === 'bubble' && e.unitId === ev.unitId));
        this.effects.push({ kind: 'bubble', x: 0, y: 0, unitId: ev.unitId, text: ev.text, life: 100, maxLife: 100 });
        break;
      case 'reset':
        this.effects = [];
        this.particles = new ParticleSystem();
        // Fresh ground: repaint so last fight's craters are gone.
        this.rebuildGround(view);
        break;
      case 'panel':
        this.particles.burst(ev.x, ev.y, 22, LOG_BOTTOM, 7, this.rng, 50, 4);
        this.particles.burst(ev.x, ev.y, 10, '#6b4423', 4, this.rng, 40, 3);
        for (let i = 0; i < 4; i++) this.particles.puff(ev.x, ev.y, (this.rng() - 0.5) * 1.2, -0.5, 7, this.rng, '#9aa0a8', 40);
        break;
      default:
        break;
    }
  }

  private colorForTeam(team: number, view: ViewState): string {
    const t = view.teams.find((x) => x.team === team);
    return t?.isEnemyAi ? ENEMY_COLOR : TEAM_COLORS[team % TEAM_COLORS.length];
  }

  // ---------- backdrop ----------

  /** Returns true when a painted backdrop was drawn instead of the drawn sky. */
  private drawSky(ctx: CanvasRenderingContext2D, now: number): boolean {
    const style = this.level.style;
    const w = this.cw;
    const h = this.ch;
    const view = { camX: this.cam.x, camY: this.cam.y, zoom: this.cam.zoom, w, h, baseY: this.horizonBaseY(), now };
    if (this.backdrop.draw(ctx, view, this.level.width, this.level.height)) return true;
    const sky = ctx.createLinearGradient(0, 0, 0, h);
    sky.addColorStop(0, style.sky[0]);
    sky.addColorStop(1, style.sky[1]);
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, w, h);

    if (style.horizon === 'moon') {
      // Stars, fixed to the sky, and a distant Earth.
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      for (let i = 0; i < 90; i++) {
        const sx = (((i * 7919) % 1000) / 1000) * w;
        const sy = (((i * 104729) % 1000) / 1000) * h * 0.6;
        ctx.globalAlpha = 0.6 + 0.4 * Math.sin(now / 700 + i);
        ctx.fillRect(sx, sy, 2, 2);
      }
      ctx.globalAlpha = 1;
      const ex = w * 0.78;
      const ey = h * 0.18;
      const g = ctx.createRadialGradient(ex - 10, ey - 10, 4, ex, ey, 34);
      g.addColorStop(0, '#9ad7ff');
      g.addColorStop(0.6, '#2f7fd6');
      g.addColorStop(1, '#0b2a5a');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(ex, ey, 34, 0, Math.PI * 2);
      ctx.fill();
    } else if (this.bright) {
      drawSun(ctx, w * 0.84, h * 0.17, Math.min(w, h) * 0.055, now);
    }

    this.drawClouds(ctx, now);
    this.drawHorizon(ctx, now);
    return false;
  }

  private drawClouds(ctx: CanvasRenderingContext2D, now: number): void {
    const style = this.level.style;
    if (style.horizon === 'moon') return;
    const z = this.cam.zoom;
    const w = this.cw;
    const h = this.ch;
    const span = this.level.width + 1200;
    let color = '#ffffff';
    let under = '#cfe4ff';
    if (style.horizon === 'storm') {
      color = mix(style.sky[1], '#ffffff', 0.25);
      under = mix(style.sky[0], '#ffffff', 0.1);
    } else if (!this.bright) {
      color = mix(style.sky[1], '#ffffff', 0.35);
      under = mix(style.sky[0], '#ffffff', 0.2);
    }
    for (const c of this.clouds) {
      const drift = ((now / 1000) * 6 * c.depth) % span;
      let wx = c.x + drift - this.cam.x * c.depth;
      wx = ((((wx + 600) % span) + span) % span) - 600;
      const sx = wx * z + w / 2 - (this.level.width / 2) * z * (1 - c.depth);
      const sy = c.y * z + h * 0.08 - (this.cam.y - this.level.height * 0.6) * z * c.depth * 0.6;
      if (sx < -200 * z || sx > w + 200 * z) continue;
      drawCloud(ctx, sx, sy, c.s * z, color, under);
    }
  }

  /** Screen y of the backdrop's ground line: islands sit on the waterline; over a pit the ridge hangs where the ground would be. */
  private horizonBaseY(): number {
    const h = this.ch;
    return this.level.waterY !== null
      ? (this.level.waterY + WATER_DROP - this.cam.y) * this.cam.zoom + h / 2 + 4
      : h * 0.62 - (this.cam.y - this.level.height * 0.6) * this.cam.zoom * 0.25;
  }

  private sceneryView(now: number): SceneryView {
    return { camX: this.cam.x, camY: this.cam.y, zoom: this.cam.zoom, w: this.cw, h: this.ch, baseY: this.horizonBaseY(), now };
  }

  private drawHorizon(ctx: CanvasRenderingContext2D, now: number): void {
    const style = this.level.style;
    const w = this.cw;
    const h = this.ch;
    const par = (f: number) => -((this.cam.x - this.level.width / 2) * f * this.cam.zoom);
    const baseY = this.horizonBaseY();
    const layers: [string, number, number][] = [
      [style.far, 0.08, 1],
      [style.near, 0.16, 1.6],
    ];
    layers.forEach(([color, f, amp], li) => {
      const pts: [number, number][] = [];
      const off = par(f);
      for (let x = -40; x <= w + 40; x += 24) {
        const wx = (x - off) / this.cam.zoom;
        let y = baseY;
        switch (style.horizon) {
          case 'islands':
            y += Math.sin(wx * 0.004) * 10 * amp + Math.max(0, Math.sin(wx * ISLAND_K + 1)) * -62 * amp;
            break;
          case 'mesas': {
            const step = Math.floor(wx / 420);
            const r = (Math.sin(step * 12.9898) * 43758.5453) % 1;
            y += -80 * amp * Math.abs(r) - 20 + Math.sin(wx * 0.02) * 4;
            break;
          }
          case 'city': {
            const step = Math.floor(wx / 90);
            const r = Math.abs((Math.sin(step * 7.123) * 10000) % 1);
            y += -140 * amp * r;
            break;
          }
          case 'storm':
            y += Math.sin(wx * 0.006 + now / 900) * 18 * amp + Math.sin(wx * 0.0021) * 40 * amp;
            break;
          case 'moon': {
            const step = Math.floor(wx / 260);
            const r = Math.abs((Math.sin(step * 5.731) * 10000) % 1);
            y += -40 * amp * r + Math.sin(wx * 0.01) * 6;
            break;
          }
          case 'dunes':
            y += Math.sin(wx * 0.003) * 36 * amp + Math.sin(wx * 0.011 + 2) * 10 * amp;
            break;
        }
        pts.push([x, y]);
      }
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(-40, h);
      for (const [x, y] of pts) ctx.lineTo(x, y);
      ctx.lineTo(w + 40, h);
      ctx.closePath();
      ctx.fill();
      // Cel highlight along the ridge.
      ctx.strokeStyle = mix(color, '#ffffff', 0.22);
      ctx.lineWidth = 4 * Math.max(0.6, this.cam.zoom);
      ctx.lineJoin = 'round';
      ctx.beginPath();
      pts.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
      ctx.stroke();

      if (style.horizon === 'islands' && li === 1) {
        // Palms on the nearer island tops.
        const k = ISLAND_K;
        const leaf = shade(color, -28);
        const trunk = shade(color, -45);
        for (let n = -4; n <= 16; n++) {
          const wxPeak = (Math.PI / 2 - 1 + n * Math.PI * 2) / k;
          const sxPeak = wxPeak * this.cam.zoom + off;
          if (sxPeak < -80 || sxPeak > w + 80) continue;
          const yPeak = baseY + Math.sin(wxPeak * 0.004) * 10 * amp - 62 * amp;
          const hp = 46 * Math.max(0.7, this.cam.zoom);
          drawPalm(ctx, sxPeak - hp * 0.5, yPeak + 6, hp, trunk, leaf, 1);
          drawPalm(ctx, sxPeak + hp * 0.4, yPeak + 10, hp * 0.8, trunk, leaf, -1);
        }
      }
    });
  }

  /** The bomber that answers an airstrike: it crosses the top of the screen the way the caller faces. Screen space, so it is always in view. */
  private drawPlanes(ctx: CanvasRenderingContext2D, now: number): void {
    for (const e of this.effects) {
      if (e.kind !== 'strike') continue;
      const t = 1 - e.life / e.maxLife;
      const dir = e.dir ?? 1;
      const z = clamp(this.cw / 1000, 0.8, 2.2);
      const sx = dir > 0 ? -60 * z + t * (this.cw + 120 * z) : this.cw + 60 * z - t * (this.cw + 120 * z);
      const sy = 56 * z + Math.sin(t * 18) * 3 * z;
      ctx.save();
      ctx.translate(sx, sy);
      ctx.scale(z * dir, z);
      drawPlane(ctx, now / 25);
      ctx.restore();
    }
  }

  // ---------- field ----------

  private drawDeep(ctx: CanvasRenderingContext2D): void {
    const lv = this.level;
    const top = lv.waterY !== null ? lv.waterY + WATER_DROP : lv.height - 260;
    const g = ctx.createLinearGradient(0, top, 0, lv.height + 400);
    g.addColorStop(0, 'rgba(0,0,0,0)');
    g.addColorStop(0.35, lv.style.deep);
    g.addColorStop(1, '#000');
    ctx.fillStyle = g;
    ctx.fillRect(-2000, top, lv.width + 4000, lv.height + 1200 - top);
  }

  private surfaceAt(x: number, waterY: number, now: number): number {
    return waterY + Math.sin(x * 0.015 + now / 450) * 5 + Math.sin(x * 0.04 - now / 650) * 3;
  }

  private drawWater(ctx: CanvasRenderingContext2D, waterY: number, now: number): void {
    const lv = this.level;
    const x0 = -2000;
    const x1 = lv.width + 2000;
    const tracePath = (dy: number) => {
      ctx.beginPath();
      for (let x = x0; x <= x1; x += 28) {
        const y = this.surfaceAt(x, waterY, now) + dy;
        if (x === x0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
    };
    // Body
    tracePath(0);
    ctx.lineTo(x1, lv.height + 800);
    ctx.lineTo(x0, lv.height + 800);
    ctx.closePath();
    ctx.fillStyle = lv.style.water;
    ctx.fill();
    // Glassy band just under the surface
    tracePath(0);
    for (let x = x1; x >= x0; x -= 28) ctx.lineTo(x, this.surfaceAt(x, waterY, now) + 18);
    ctx.closePath();
    ctx.fillStyle = 'rgba(255,255,255,0.14)';
    ctx.fill();
    // Drifting highlight dashes
    ctx.strokeStyle = 'rgba(255,255,255,0.22)';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    const span = lv.width + 4000;
    for (let i = 0; i < 40; i++) {
      const dx = ((i * 373 + now * 0.025 * (1 + (i % 3) * 0.4)) % span) + x0;
      const dy = waterY + 34 + ((i * 97) % 140);
      const len = 16 + (i % 3) * 12;
      ctx.beginPath();
      ctx.moveTo(dx, dy);
      ctx.lineTo(dx + len, dy);
      ctx.stroke();
    }
    // Crest: a dark seam with white foam riding it
    tracePath(3);
    ctx.strokeStyle = 'rgba(8, 40, 80, 0.45)';
    ctx.lineWidth = 4;
    ctx.stroke();
    tracePath(0);
    ctx.strokeStyle = 'rgba(255,255,255,0.88)';
    ctx.lineWidth = 3.5;
    ctx.stroke();
  }

  /**
   * The map's catapults, with the launches they have left shown as tally pegs on a post beside
   * them. Cocked while launches remain, left against the stop once spent; a 'catapult' event
   * swings the arm over as the mole is flung.
   */
  private drawCatapults(ctx: CanvasRenderingContext2D, view: ViewState): void {
    if (view.catapults.length === 0) return;
    for (const c of view.catapults) {
      const swingFx = this.effects.find((e) => e.kind === 'catapult' && e.id === c.id);
      const rest = c.boulders > 0 ? 0 : 1;
      const swing = swingFx ? catapultSwing(1 - swingFx.life / swingFx.maxLife, rest) : rest;
      ctx.save();
      ctx.translate(c.x, c.y);
      ctx.scale(c.dir, 1);
      // The tally post: one peg per launch left.
      rr(ctx, -78, -46, 8, 46, 2);
      ink(ctx, '#8a5a2a', 2);
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        ctx.arc(-74, -38 + i * 12, 4.5, 0, Math.PI * 2);
        ink(ctx, i < c.boulders ? '#ffcc4d' : '#4b3a2a', 1.5);
      }
      drawCatapult(ctx, swing, false);
      ctx.restore();
    }
  }

  /** Things left in the ground: graves, and crossbow bolts for a few seconds. */
  private drawGraves(ctx: CanvasRenderingContext2D): void {
    for (const e of this.effects) {
      if (e.kind === 'grave') drawGrave(ctx, e.x, e.y, e.color ?? '#fff');
      else if (e.kind === 'bolt') {
        ctx.save();
        ctx.globalAlpha = Math.min(1, e.life / 30);
        ctx.translate(e.x, e.y);
        ctx.rotate(e.angle ?? 0);
        // Sunk to the shaft: the tip is in the ground and the fletching stands out of it.
        ctx.translate(-12, 0);
        drawBolt(ctx);
        ctx.restore();
      }
    }
  }

  private drawUnits(ctx: CanvasRenderingContext2D, input: RenderInput): void {
    const { view } = input;
    const t = performance.now();
    for (const u of view.units) {
      if (!u.alive) continue;
      const team = view.teams.find((x) => x.team === u.team);
      const color = team?.isEnemyAi ? ENEMY_COLOR : TEAM_COLORS[u.team % TEAM_COLORS.length];
      const look: MoleLook = this.looks.get(u.playerId) ?? {
        // Raiders are dressed as a mix of creatures, the same ones on every client (the unit id picks the race).
        race: u.isBot ? raceForKey(u.id) : undefined,
        body: u.isBot ? 'body.stocky' : 'body.lean',
        outfit: u.isBot ? 'outfit.bandana' : 'outfit.none',
        teamColor: color,
        furColor: u.isBot ? '#7a6660' : undefined,
      };
      const active = u.id === (view.turn.unitId ?? input.focusUnitId);
      const isLocal = u.playerId === input.localPlayerId;
      const phase = view.turn.phase;
      const live = phase === 'live';
      let pose: MolePose = u.pose;
      let aim: number | undefined;
      let held: HeldItem | null = (u.held as HeldItem | null) ?? null;
      // Brawl: the local mole winds up while its player is dragging an aim; the held thing is its own.
      const aimedAction = live ? (active && input.aim && input.aim.unitId === u.id && !u.ragdoll ? u.action : null) : view.turn.action;
      if (active && (phase === 'aim' || live) && aimedAction) {
        const kind = ACTIONS[aimedAction].kind;
        aim = input.aim && input.aim.unitId === u.id ? input.aim.angle : view.turn.aimAngle;
        if (kind === 'throw') {
          pose = 'throw';
          held = aimedAction as HeldItem;
        } else if (kind === 'hitscan') pose = 'gun';
      } else if (u.pose === 'throw' || u.pose === 'gun') {
        aim = u.aim;
      }

      // Selection ring for the local player's moles while picking.
      if (input.selecting && isLocal && !active && phase === 'select') {
        ctx.strokeStyle = 'rgba(255,255,255,0.8)';
        ctx.setLineDash([5, 5]);
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.ellipse(u.x, u.y - 2, UNIT_HALF_W + 10, 7, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      if (u.floatie) this.drawFloatie(ctx, u.x, u.y, color, t, u.facing);
      if (active && phase !== 'between' && phase !== 'ended') {
        ctx.fillStyle = 'rgba(0,0,0,0.22)';
        ctx.beginPath();
        ctx.ellipse(u.x, u.y + 1, UNIT_HALF_W + 8, 5, 0, 0, Math.PI * 2);
        ctx.fill();
      }

      drawMole(ctx, u.x, u.y, { ...look, teamColor: color }, {
        facing: u.facing, pose, aim, walkPhase: u.walkPhase, spin: u.spin, flash: u.hitFlash > 0 && u.hitFlash % 4 < 2, held,
      });

      // Name + HP pill
      ctx.font = '700 13px Nunito, system-ui, sans-serif';
      ctx.textAlign = 'center';
      const hpW = 48;
      const ly = u.y - UNIT_H - 32;
      rr(ctx, u.x - hpW / 2, ly - 4, hpW, 10, 5);
      ink(ctx, 'rgba(255,255,255,0.9)', 2);
      ctx.fillStyle = color;
      rr(ctx, u.x - hpW / 2 + 1.5, ly - 2.5, Math.max(0, (hpW - 3) * (u.hp / u.maxHp)), 7, 3.5);
      ctx.fill();
      ctx.fillStyle = isLocal ? '#ffffff' : 'rgba(255,255,255,0.9)';
      ctx.strokeStyle = 'rgba(20,12,8,0.75)';
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.strokeText(u.name, u.x, ly - 9);
      ctx.fillText(u.name, u.x, ly - 9);

      if (active && (phase === 'select' || phase === 'move' || phase === 'aim' || live)) {
        const bob = Math.sin(t / 160) * 4;
        ctx.beginPath();
        ctx.moveTo(u.x, ly - 27 + bob);
        ctx.lineTo(u.x - 10, ly - 42 + bob);
        ctx.lineTo(u.x + 10, ly - 42 + bob);
        ctx.closePath();
        ink(ctx, color, 2);
      }

      if (live && active && isLocal) {
        // The sim keeps only the ticks left; the cooldown's length is its peak since it last hit zero.
        this.coolPeak = u.cooldown > 0 ? Math.max(this.coolPeak, u.cooldown) : 0;
        if (u.cooldown > 0) this.drawCooldown(ctx, u.x, ly + 15, u.cooldown / this.coolPeak);
      }
    }
  }

  /** Brawl: a small ring between the HP pill and the head that fills as the held thing's cooldown runs out. */
  private drawCooldown(ctx: CanvasRenderingContext2D, x: number, y: number, left: number): void {
    const done = 1 - clamp(left, 0, 1);
    ctx.save();
    ctx.lineCap = 'round';
    ctx.strokeStyle = 'rgba(20,12,8,0.6)';
    ctx.lineWidth = 4.5;
    ctx.beginPath();
    ctx.arc(x, y, 7, 0, Math.PI * 2);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = 2.5;
    ctx.stroke();
    if (done > 0) {
      ctx.strokeStyle = '#ffcc4d';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(x, y, 7, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * done);
      ctx.stroke();
    }
    ctx.restore();
  }

  /** A life ring (or a jetski, on the maps that say so) on water levels; a balloon-held crate over a pit. */
  private drawFloatie(ctx: CanvasRenderingContext2D, x: number, y: number, team: string, now: number, facing: 1 | -1): void {
    const bob = Math.sin(now / 420 + x * 0.01) * 2;
    if (this.level.waterY !== null && this.level.floatStyle === 'jetski') {
      ctx.save();
      ctx.translate(x, y + 8 + WATER_DROP * 0.6 + bob);
      ctx.scale(facing, 1);
      // The wake and spray off the stern
      ctx.fillStyle = 'rgba(255,255,255,0.35)';
      ctx.beginPath();
      ctx.moveTo(-32, 6);
      ctx.lineTo(-78, -2);
      ctx.lineTo(-78, 12);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      for (let i = 0; i < 5; i++) {
        const ph = (now / 110 + i * 1.3) % 3;
        ctx.beginPath();
        ctx.arc(-38 - i * 7 - ph * 4, 2 - Math.sin(ph) * 9 - i * 2, 2.5 + (i % 2), 0, Math.PI * 2);
        ctx.fill();
      }
      drawJetski(ctx, team);
      ctx.restore();
    } else if (this.level.waterY !== null) {
      ctx.save();
      ctx.translate(x, y + 6 + WATER_DROP * 0.6 + bob);
      ctx.beginPath();
      ctx.ellipse(0, 0, 32, 12, 0, 0, Math.PI * 2);
      ctx.lineWidth = 15;
      ctx.strokeStyle = INK;
      ctx.stroke();
      ctx.lineWidth = 11;
      ctx.strokeStyle = '#f4f1e6';
      ctx.stroke();
      ctx.strokeStyle = team;
      ctx.setLineDash([16, 18]);
      ctx.lineDashOffset = 6;
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    } else {
      ctx.save();
      ctx.translate(x, y + bob);
      rr(ctx, -26, 0, 52, 12, 2);
      ink(ctx, '#c98c4e');
      ctx.strokeStyle = 'rgba(255,255,255,0.7)';
      ctx.lineWidth = 1.5;
      for (const sx of [-18, 18]) {
        ctx.beginPath();
        ctx.moveTo(sx, 0);
        ctx.lineTo(sx * 1.3, -UNIT_H - 26);
        ctx.stroke();
      }
      for (const sx of [-24, 24]) {
        ctx.beginPath();
        ctx.ellipse(sx, -UNIT_H - 38, 14, 18, 0, 0, Math.PI * 2);
        ink(ctx, team, 2);
        ctx.fillStyle = 'rgba(255,255,255,0.4)';
        ctx.beginPath();
        ctx.ellipse(sx - 5, -UNIT_H - 44, 4, 6, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
  }

  /** The team's previous shot, faint, while one of its moles is picking or aiming. Shop kit. */
  private drawGhost(ctx: CanvasRenderingContext2D, input: RenderInput): void {
    const { view } = input;
    if (view.turn.phase !== 'aim' && view.turn.phase !== 'select' && view.turn.phase !== 'live') return;
    const u = view.units.find((x) => x.id === (view.turn.unitId ?? input.focusUnitId) && x.alive);
    if (!u || u.playerId !== input.localPlayerId) return;
    const team = view.teams.find((t) => t.team === u.team);
    const ghost = team?.items?.ghost ? team.lastShot : null;
    if (!ghost || ghost.path.length < 2) return;
    const end = ghost.path[ghost.path.length - 1]!;
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.5)';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.setLineDash([3, 8]);
    ctx.beginPath();
    ctx.moveTo(ghost.path[0]![0], ghost.path[0]![1]);
    for (const [x, y] of ghost.path.slice(1)) ctx.lineTo(x, y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.arc(end[0], end[1], 9, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.font = '800 12px Nunito, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('last shot', end[0], end[1] - 14);
    ctx.restore();
  }

  /** The first thing under world x: ground, or a deck that is higher than it. The bottom of the map (or the water) when there is nothing. */
  private groundUnder(view: ViewState, x: number): number {
    let y = view.terrain.surfaceY(x, 0);
    for (const b of view.boats) {
      const d = deckY(b, x);
      if (d !== null && d < y) y = d;
    }
    if (y >= view.level.height && view.level.waterY !== null) y = view.level.waterY;
    return y;
  }

  /** A faint dotted line from the mole's middle to where a placed thing goes. */
  private placeLine(ctx: CanvasRenderingContext2D, ux: number, uy: number, x: number, y: number): void {
    ctx.setLineDash([2, 6]);
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.beginPath();
    ctx.moveTo(ux, uy - UNIT_H * 0.7);
    ctx.lineTo(x, y);
    ctx.stroke();
  }

  private drawAim(ctx: CanvasRenderingContext2D, input: RenderInput, now: number): void {
    const { view } = input;
    let action = view.turn.action;
    let u = view.units.find((x) => x.id === view.turn.unitId && x.alive);
    if (view.turn.phase === 'live') {
      // Brawl: the preview is the local mole's, for what it holds, only while its player is aiming.
      u = view.units.find((x) => x.id === input.focusUnitId && x.alive);
      if (!u || !input.aim || input.aim.unitId !== u.id) return;
      action = u.action;
    } else if (view.turn.phase !== 'aim') return;
    if (!u || !action) return;
    const def = ACTIONS[action];
    const isLocal = u.playerId === input.localPlayerId;
    const angle = input.aim && input.aim.unitId === u.id ? input.aim.angle : view.turn.aimAngle;
    const power = input.aim && input.aim.unitId === u.id ? input.aim.power : view.turn.aimPower;
    const dirX = Math.cos(angle) * u.facing;
    const dirY = -Math.sin(angle);

    if (def.dig) {
      // Where the pickaxe will bite: a row of rings along the aim.
      ctx.setLineDash([5, 6]);
      ctx.strokeStyle = isLocal ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.4)';
      ctx.lineWidth = 2;
      for (let i = 1; i <= DIG_BITES; i++) {
        ctx.beginPath();
        ctx.arc(u.x + dirX * (i * DIG_STEP + 6), u.y - UNIT_H * 0.5 + dirY * (i * DIG_STEP + 6), DIG_RADIUS - 2, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.setLineDash([]);
    } else if (def.strike) {
      // Where the bombs come down: a line from the sky to the marked ground, chevrons running down it, a ring per bomb.
      const x = strikeSpot(view.level, u, angle, power);
      const groundY = this.groundUnder(view, x);
      ctx.save();
      ctx.lineCap = 'round';
      ctx.setLineDash([10, 8]);
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = isLocal ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)';
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, groundY);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.lineWidth = 3;
      ctx.strokeStyle = isLocal ? 'rgba(255,221,77,0.9)' : 'rgba(255,221,77,0.5)';
      const scroll = (now / 8) % 60;
      for (let y = 30 + scroll; y < groundY - 24; y += 60) {
        ctx.beginPath();
        ctx.moveTo(x - 9, y - 9);
        ctx.lineTo(x, y);
        ctx.lineTo(x + 9, y - 9);
        ctx.stroke();
      }
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 2;
      ctx.strokeStyle = isLocal ? 'rgba(255,90,90,0.9)' : 'rgba(255,90,90,0.5)';
      const n = def.strike;
      for (let i = 0; i < n; i++) {
        const bx = x + (i - (n - 1) / 2) * STRIKE_GAP * u.facing;
        const by = this.groundUnder(view, bx);
        ctx.beginPath();
        ctx.arc(bx, by - 4, 10, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();
    } else if (def.place === 'self') {
      // Where the mole lands: its own outline, red where the blink is refused.
      const spot = teleportSpot(view.level, (x, y) => view.terrain.isSolid(x, y), u, angle, power);
      ctx.save();
      this.placeLine(ctx, u.x, u.y, spot.x, spot.y - UNIT_H * 0.5);
      ctx.setLineDash([6, 5]);
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = spot.ok ? 'rgba(255,255,255,0.85)' : 'rgba(255,80,80,0.9)';
      ctx.fillStyle = spot.ok ? 'rgba(140,236,255,0.18)' : 'rgba(255,80,80,0.18)';
      rr(ctx, spot.x - UNIT_HALF_W, spot.y - UNIT_H * 0.6, UNIT_HALF_W * 2, UNIT_H * 0.6, 8);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(spot.x, spot.y - UNIT_H * 0.78, UNIT_H * 0.24, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      if (!spot.ok) {
        ctx.setLineDash([]);
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(spot.x - 12, spot.y - UNIT_H * 0.5 - 12);
        ctx.lineTo(spot.x + 12, spot.y - UNIT_H * 0.5 + 12);
        ctx.moveTo(spot.x + 12, spot.y - UNIT_H * 0.5 - 12);
        ctx.lineTo(spot.x - 12, spot.y - UNIT_H * 0.5 + 12);
        ctx.stroke();
      }
      ctx.restore();
    } else if (def.place === 'charge') {
      // Where the charge is set down: a small box, crossed out where it would sit on someone.
      const spot = platformSpot(view.level, view.units, u, angle, power, 24);
      ctx.save();
      this.placeLine(ctx, u.x, u.y, spot.x, spot.y);
      ctx.setLineDash([5, 4]);
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = spot.ok ? 'rgba(255,255,255,0.85)' : 'rgba(255,80,80,0.9)';
      ctx.fillStyle = spot.ok ? 'rgba(214,54,44,0.3)' : 'rgba(255,80,80,0.2)';
      rr(ctx, spot.x - 12, spot.y - PLATFORM_H / 2, 24, PLATFORM_H, 3);
      ctx.fill();
      ctx.stroke();
      if (!spot.ok) {
        ctx.setLineDash([]);
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(spot.x - 14, spot.y - 14);
        ctx.lineTo(spot.x + 14, spot.y + 14);
        ctx.moveTo(spot.x + 14, spot.y - 14);
        ctx.lineTo(spot.x - 14, spot.y + 14);
        ctx.stroke();
      }
      ctx.restore();
    } else if (def.place) {
      // Where the plank (or the long bridge) will sit: a dashed plank, red if it would cut through someone.
      const w = def.span ?? PLATFORM_W;
      const spot = platformSpot(view.level, view.units, u, angle, power, w);
      ctx.save();
      ctx.setLineDash([6, 5]);
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = spot.ok ? 'rgba(255,255,255,0.85)' : 'rgba(255,80,80,0.9)';
      ctx.fillStyle = spot.ok ? 'rgba(201,140,78,0.35)' : 'rgba(255,80,80,0.2)';
      rr(ctx, spot.x - w / 2, spot.y - PLATFORM_H / 2, w, PLATFORM_H, 3);
      ctx.fill();
      ctx.stroke();
      this.placeLine(ctx, u.x, u.y, spot.x, spot.y);
      ctx.restore();
    } else if (def.kind === 'throw') {
      // Dotted preview of the first part of the arc, Raft Wars style. A boulder leaves the
      // catapult's cup and the power arc wraps the catapult; everything else leaves the hand.
      const team = view.teams.find((x) => x.team === u.team);
      const mult = team?.perks.throwMultiplier ?? 1;
      const speed = launchSpeed(def, power, mult);
      const station = def.station ? catapultNear(view.catapults, u) : null;
      let x = station ? station.x + station.dir * 6 : u.x + u.facing * 10 + dirX * 24;
      let y = station ? station.y - CATAPULT_CUP_H : u.y - UNIT_H * 0.78 + dirY * 24;
      let vx = dirX * speed;
      let vy = dirY * speed;
      // A hook's preview stops where the hook would be reeled back in.
      const dots = def.fixedPower ? 11 : isLocal ? 22 : 12;
      for (let i = 0; i < dots * 3; i++) {
        vy += def.gravity * view.level.gravity;
        x += vx;
        y += vy;
        if (view.terrain.isSolid(x, y) || view.boats.some((b) => boatContains(b, x, y))) break;
        if (i % 3 === 0) {
          const r = 4.5 - (i / (dots * 3)) * 2.5;
          ctx.globalAlpha = (isLocal ? 1 : 0.5) * (1 - i / (dots * 3));
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ink(ctx, '#ffffff', 1.5);
        }
      }
      ctx.globalAlpha = 1;
      if (!def.fixedPower) {
        // Power arc around the thrower (or the catapult being wound).
        const cx = station ? station.x : u.x;
        const cy = station ? station.y - 44 : u.y - UNIT_H * 0.6;
        const cr = station ? 58 : 34;
        ctx.lineCap = 'round';
        ctx.strokeStyle = INK;
        ctx.lineWidth = 7;
        ctx.beginPath();
        ctx.arc(cx, cy, cr, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * power);
        ctx.stroke();
        ctx.strokeStyle = power > 0.85 ? '#ff5d5d' : '#ffcc4d';
        ctx.lineWidth = 4;
        ctx.stroke();
      }
    } else if (def.kind === 'hitscan') {
      const ox = u.x + u.facing * 14 + dirX * 26;
      const oy = u.y - UNIT_H * 0.62 + dirY * 26;
      const reach = def.range ?? 2000;
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.setLineDash([3, 9]);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(ox, oy);
      ctx.lineTo(ox + dirX * reach, oy + dirY * reach);
      ctx.stroke();
      ctx.setLineDash([]);
      if (def.range) {
        // Where a short gun stops biting.
        ctx.beginPath();
        ctx.arc(ox + dirX * reach, oy + dirY * reach, 5, 0, Math.PI * 2);
        ink(ctx, 'rgba(255,255,255,0.5)', 1.5);
      }
      const target = isLocal && input.pointer ? input.pointer : { x: ox + dirX * 300, y: oy + dirY * 300 };
      ctx.lineCap = 'round';
      for (const [col, lw] of [[INK, 4.5], ['#ff5d5d', 2.2]] as [string, number][]) {
        ctx.strokeStyle = col;
        ctx.lineWidth = lw;
        ctx.beginPath();
        ctx.arc(target.x, target.y, 14, 0, Math.PI * 2);
        ctx.moveTo(target.x - 20, target.y);
        ctx.lineTo(target.x - 8, target.y);
        ctx.moveTo(target.x + 8, target.y);
        ctx.lineTo(target.x + 20, target.y);
        ctx.moveTo(target.x, target.y - 20);
        ctx.lineTo(target.x, target.y - 8);
        ctx.moveTo(target.x, target.y + 8);
        ctx.lineTo(target.x, target.y + 20);
        ctx.stroke();
      }
    }
  }

  private drawProjectiles(ctx: CanvasRenderingContext2D, view: ViewState, now: number): void {
    for (const p of view.projectiles) {
      if (!p.alive) continue;
      switch (p.weapon) {
        case 'ball':
          drawTennisBall(ctx, p.x, p.y, 8, p.age * 0.2);
          break;
        case 'grenade': {
          drawGrenade(ctx, p.x, p.y, 8, p.age * 0.15);
          this.fuseLabel(ctx, p.x, p.y - 22, p.fuse);
          break;
        }
        case 'rpg':
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(Math.atan2(p.vy, p.vx));
          drawRocket(ctx, p.homing, 0.6 + 0.4 * Math.sin(now / 30));
          ctx.restore();
          break;
        case 'mortar':
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(Math.atan2(p.vy, p.vx));
          drawShell(ctx);
          ctx.restore();
          break;
        case 'bignade': {
          drawBigGrenade(ctx, p.x, p.y, 12, p.age * 0.12);
          this.fuseLabel(ctx, p.x, p.y - 28, p.fuse);
          break;
        }
        case 'cluster':
          drawCluster(ctx, p.x, p.y, p.frag ? 5 : 9, p.age * 0.2, !!p.frag);
          break;
        case 'mine':
          drawMine(ctx, p.x, p.y, 9, false, 0);
          break;
        case 'grapple': {
          const thrower = view.units.find((u) => u.id === p.unitId);
          if (thrower) this.rope(ctx, thrower.x + thrower.facing * 10, thrower.y - UNIT_H * 0.7, p.x, p.y);
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(Math.atan2(p.vy, p.vx));
          drawHook(ctx);
          ctx.restore();
          break;
        }
        // A stuck sticky already rides its mole (the sim moves it), so it is drawn where it says.
        case 'sticky':
          drawSticky(ctx, p.x, p.y, 9, 0.5 + 0.5 * Math.sin(now / 90));
          this.fuseLabel(ctx, p.x, p.y - 22, p.fuse);
          break;
        case 'rubber':
          drawRubberBall(ctx, p.x, p.y, 8, p.age * 0.25);
          this.fuseLabel(ctx, p.x, p.y - 22, p.fuse);
          break;
        case 'airstrike':
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(Math.atan2(p.vy, p.vx));
          drawAirBomb(ctx);
          ctx.restore();
          break;
        case 'massive':
          drawMassive(ctx, p.x, p.y, 18, p.age * 0.06, 0.5 + 0.5 * Math.sin(now / 40));
          this.fuseLabel(ctx, p.x, p.y - 42, p.fuse);
          break;
        case 'crossbow':
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(Math.atan2(p.vy, p.vx));
          drawBolt(ctx);
          ctx.restore();
          break;
        case 'tnt':
          drawTnt(ctx, p.x, p.y, 1, 0.5 + 0.5 * Math.sin(now / 45));
          this.fuseLabel(ctx, p.x, p.y - 30, p.fuse);
          break;
        case 'catapult':
          drawBoulder(ctx, p.x, p.y, 13, p.age * 0.1);
          break;
        case 'tunneler':
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(Math.atan2(p.vy, p.vx));
          // Heading left it is flipped over its line of travel, so the cab stays on top.
          if (p.vx < 0) ctx.scale(1, -1);
          drawTunneler(ctx, (p.age * 0.09) % 1, p.age * 0.2);
          ctx.restore();
          break;
        // Anything the renderer has no picture for still flies as a dark ball.
        default:
          ctx.beginPath();
          ctx.arc(p.x, p.y, 7, 0, Math.PI * 2);
          ink(ctx, '#3a3f47', 2.5);
          break;
      }
    }
  }

  private fuseLabel(ctx: CanvasRenderingContext2D, x: number, y: number, fuse: number): void {
    ctx.fillStyle = '#fff';
    ctx.font = '800 12px Nunito, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.strokeStyle = 'rgba(0,0,0,0.7)';
    ctx.lineWidth = 3;
    const s = String(Math.max(1, Math.ceil(fuse / 60)));
    ctx.strokeText(s, x, y);
    ctx.fillText(s, x, y);
  }

  /** A taut rope: ink under a pale core, with a little sag. */
  private rope(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number): void {
    const mx = (x0 + x1) / 2;
    const my = (y0 + y1) / 2 + Math.min(18, Math.hypot(x1 - x0, y1 - y0) * 0.04);
    ctx.lineCap = 'round';
    for (const [col, lw] of [[INK, 4.5], [ROPE, 2.2]] as [string, number][]) {
      ctx.strokeStyle = col;
      ctx.lineWidth = lw;
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.quadraticCurveTo(mx, my, x1, y1);
      ctx.stroke();
    }
  }

  /** Lines from moles on a line to where it leads: a hooked mole to its hook, a harpooned one to the gun. */
  private drawRopes(ctx: CanvasRenderingContext2D, view: ViewState): void {
    for (const u of view.units) {
      if (!u.alive || !u.pull) continue;
      const hooked = u.pull.left > 1e5;
      this.rope(ctx, u.x, u.y - UNIT_H * 0.6, u.pull.x, u.pull.y);
      if (hooked) {
        ctx.save();
        ctx.translate(u.pull.x, u.pull.y);
        ctx.rotate(Math.atan2(u.pull.y - (u.y - UNIT_H * 0.6), u.pull.x - u.x));
        drawHook(ctx, 0.9);
        ctx.restore();
      }
    }
    for (const e of this.effects) {
      if (e.kind !== 'hook') continue;
      ctx.globalAlpha = Math.min(1, e.life / 12);
      ctx.save();
      ctx.translate(e.x, e.y);
      drawHook(ctx, 0.9);
      ctx.restore();
      ctx.globalAlpha = 1;
    }
  }

  private drawMines(ctx: CanvasRenderingContext2D, view: ViewState, now: number): void {
    for (const m of view.mines) {
      const hot = m.fuse >= 0 ? (Math.floor(now / 70) % 2 === 0 ? 1 : 0) : m.armed ? (Math.sin(now / 350) > 0.6 ? 1 : 0) : 0;
      drawMine(ctx, m.x, m.y - 2, 10, m.armed, hot);
    }
  }

  private drawShots(ctx: CanvasRenderingContext2D): void {
    for (const e of this.effects) {
      if (e.kind === 'shot' && e.weapon === 'harpoon') {
        // The line pays out to wherever the spear stopped, with the spearhead at its end.
        const a = Math.min(1, e.life / 8);
        ctx.globalAlpha = a;
        this.rope(ctx, e.x, e.y, e.x1!, e.y1!);
        ctx.save();
        ctx.translate(e.x1!, e.y1!);
        ctx.rotate(Math.atan2(e.y1! - e.y, e.x1! - e.x));
        inkLine(ctx, -16, 0, 6, 0, 4, '#8a949e', 1.5);
        ctx.beginPath();
        ctx.moveTo(14, 0);
        ctx.lineTo(2, -6);
        ctx.lineTo(2, 6);
        ctx.closePath();
        ink(ctx, '#d8dde2', 2);
        ctx.restore();
        ctx.globalAlpha = 1;
      } else if (e.kind === 'shot' && e.weapon === 'railgun') {
        // The slug's track: a cyan glow round a white core, the full line the sim gave, with sparks riding it.
        const a = e.life / e.maxLife;
        ctx.lineCap = 'round';
        for (const [col, lw] of [[`rgba(80, 220, 255, ${a * 0.4})`, 18 * (0.5 + a * 0.5)], [`rgba(160, 240, 255, ${a * 0.8})`, 7], [`rgba(255,255,255,${a})`, 3]] as [string, number][]) {
          ctx.strokeStyle = col;
          ctx.lineWidth = lw;
          ctx.beginPath();
          ctx.moveTo(e.x, e.y);
          ctx.lineTo(e.x1!, e.y1!);
          ctx.stroke();
        }
        ctx.fillStyle = `rgba(255,255,255,${a})`;
        for (let i = 0; i < 6; i++) {
          const f = (i * 0.17 + (1 - a) * 0.7) % 1;
          ctx.beginPath();
          ctx.arc(e.x + (e.x1! - e.x) * f, e.y + (e.y1! - e.y) * f, 2.5 + (i % 2), 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (e.kind === 'shot' && e.weapon === 'smg') {
        // One thin yellow tracer per round of the burst.
        const a = e.life / e.maxLife;
        ctx.lineCap = 'round';
        ctx.strokeStyle = `rgba(255, 225, 90, ${a})`;
        ctx.lineWidth = 1.8;
        ctx.beginPath();
        ctx.moveTo(e.x, e.y);
        ctx.lineTo(e.x1!, e.y1!);
        ctx.stroke();
      } else if (e.kind === 'shot') {
        const a = e.life / e.maxLife;
        ctx.lineCap = 'round';
        const ang = Math.atan2(e.y1! - e.y, e.x1! - e.x);
        const len = Math.hypot(e.x1! - e.x, e.y1! - e.y);
        // A shotgun fans three lines; the pistol and sniper draw one.
        const spreads = e.weapon === 'shotgun' ? [-0.07, 0, 0.07] : [0];
        for (const s of spreads) {
          const x1 = e.x + Math.cos(ang + s) * len;
          const y1 = e.y + Math.sin(ang + s) * len;
          ctx.strokeStyle = `rgba(60, 30, 10, ${a * 0.6})`;
          ctx.lineWidth = 5;
          ctx.beginPath();
          ctx.moveTo(e.x, e.y);
          ctx.lineTo(x1, y1);
          ctx.stroke();
          ctx.strokeStyle = `rgba(255, 240, 180, ${a})`;
          ctx.lineWidth = 2.5;
          ctx.stroke();
        }
      } else if (e.kind === 'kick') {
        const t = 1 - e.life / e.maxLife;
        ctx.strokeStyle = `rgba(255, 255, 255, ${1 - t})`;
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        for (let i = -1; i <= 1; i++) {
          ctx.beginPath();
          ctx.moveTo(e.x, e.y + i * 12);
          ctx.lineTo(e.x + (e.dir ?? 1) * (14 + t * 30), e.y + i * 18);
          ctx.stroke();
        }
        ctx.font = `800 ${20 + t * 10}px 'Lilita One', Impact, sans-serif`;
        ctx.textAlign = 'center';
        ctx.lineWidth = 5;
        ctx.strokeStyle = `rgba(42, 26, 18, ${1 - t})`;
        const word = e.weapon === 'bat' ? 'WHACK!' : e.weapon === 'mallet' ? 'THUD!' : e.weapon === 'pike' ? 'POKE!' : 'BOOT!';
        ctx.strokeText(word, e.x, e.y - 30 - t * 20);
        ctx.fillStyle = `rgba(255, 204, 77, ${1 - t})`;
        ctx.fillText(word, e.x, e.y - 30 - t * 20);
      }
    }
  }

  private drawExplosions(ctx: CanvasRenderingContext2D): void {
    for (const e of this.effects) {
      if (e.kind === 'explosion') {
        drawBurst(ctx, e.x, e.y, e.r ?? 20, 1 - e.life / e.maxLife);
      } else if (e.kind === 'splash') {
        drawSplash(ctx, e.x, e.y, 1 - e.life / e.maxLife);
      } else if (e.kind === 'boom') {
        // A vessel going up: a fireball as wide as the boat, bigger than any grenade, three bursts staggered across its length.
        const t = 1 - e.life / e.maxLife;
        const r = e.r ?? 60;
        drawBurst(ctx, e.x, e.y - r * 0.2, r * 0.8, Math.min(1, t * 1.3));
        for (const s of [-1, 1]) {
          const ts = clamp((t - 0.12) * 1.3, 0, 1);
          if (ts > 0) drawBurst(ctx, e.x + s * r * 0.55, e.y - r * 0.05, r * 0.5, ts);
        }
      } else if (e.kind === 'teleport') {
        // A ring widening out of both ends, and the mole's afterimage fading where it stood.
        const t = 1 - e.life / e.maxLife;
        ctx.save();
        ctx.lineWidth = 3;
        for (const [x, y] of [[e.x, e.y], [e.x1!, e.y1!]] as [number, number][]) {
          ctx.strokeStyle = `rgba(140, 236, 255, ${1 - t})`;
          ctx.beginPath();
          ctx.ellipse(x, y - UNIT_H * 0.5, 14 + t * 46, 20 + t * 40, 0, 0, Math.PI * 2);
          ctx.stroke();
        }
        ctx.setLineDash([5, 4]);
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = `rgba(255,255,255,${(1 - t) * 0.8})`;
        ctx.fillStyle = `rgba(140, 236, 255, ${(1 - t) * 0.25})`;
        rr(ctx, e.x - UNIT_HALF_W, e.y - UNIT_H * 0.6, UNIT_HALF_W * 2, UNIT_H * 0.6, 8);
        ctx.fill();
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(e.x, e.y - UNIT_H * 0.78, UNIT_H * 0.24, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      } else if (e.kind === 'splat') {
        // Goo biting: a green splat that spreads and fades.
        const t = 1 - e.life / e.maxLife;
        ctx.save();
        ctx.globalAlpha = 1 - t;
        ctx.fillStyle = '#7fd93a';
        ctx.beginPath();
        ctx.ellipse(e.x, e.y, 10 + t * 22, 6 + t * 10, 0, 0, Math.PI * 2);
        ctx.fill();
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2 + 0.4;
          const d = 8 + t * 30;
          ctx.beginPath();
          ctx.arc(e.x + Math.cos(a) * d, e.y + Math.sin(a) * d * 0.6, 3 + (i % 2) * 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
    }
  }

  private drawBubbles(ctx: CanvasRenderingContext2D, view: ViewState): void {
    for (const e of this.effects) {
      if (e.kind !== 'bubble') continue;
      const u = view.units.find((x) => x.id === e.unitId);
      if (!u) continue;
      const t = 1 - e.life / e.maxLife;
      const alpha = t > 0.8 ? (1 - t) / 0.2 : 1;
      ctx.globalAlpha = alpha;
      ctx.font = '800 15px Nunito, system-ui, sans-serif';
      const w = ctx.measureText(e.text ?? '').width + 20;
      const x = u.x + u.facing * 26;
      const y = u.y - UNIT_H - 60;
      ctx.beginPath();
      ctx.moveTo(x - w / 2 + 13, y - 14);
      ctx.lineTo(x + w / 2 - 13, y - 14);
      ctx.arcTo(x + w / 2, y - 14, x + w / 2, y - 1, 13);
      ctx.arcTo(x + w / 2, y + 12, x + w / 2 - 13, y + 12, 13);
      ctx.lineTo(x - u.facing * 2, y + 12);
      ctx.lineTo(x - u.facing * 18, y + 23);
      ctx.lineTo(x - u.facing * 10, y + 12);
      ctx.lineTo(x - w / 2 + 13, y + 12);
      ctx.arcTo(x - w / 2, y + 12, x - w / 2, y - 1, 13);
      ctx.arcTo(x - w / 2, y - 14, x - w / 2 + 13, y - 14, 13);
      ctx.closePath();
      ink(ctx, '#ffffff', 2.5);
      ctx.fillStyle = INK;
      ctx.textAlign = 'center';
      ctx.fillText(e.text ?? '', x, y + 4);
      ctx.globalAlpha = 1;
    }
  }

  private drawDamageNumbers(ctx: CanvasRenderingContext2D): void {
    for (const e of this.effects) {
      if (e.kind !== 'damage') continue;
      const t = 1 - e.life / e.maxLife;
      ctx.globalAlpha = 1 - Math.max(0, t - 0.6) / 0.4;
      ctx.font = `800 ${e.color === '#ffcc4d' ? 26 : 21}px 'Lilita One', Impact, sans-serif`;
      ctx.textAlign = 'center';
      ctx.lineWidth = 4.5;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(42,26,18,0.85)';
      ctx.fillStyle = e.color ?? '#fff';
      const y = e.y - t * 44;
      ctx.strokeText(e.text ?? '', e.x, y);
      ctx.fillText(e.text ?? '', e.x, y);
      ctx.globalAlpha = 1;
    }
  }
}
